import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  viewChild
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { of, startWith, switchMap, tap } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { FileUploadHandlerEvent, FileUploadModule } from '@openng/optimus-ui/fileupload';
import { InputNumberModule } from '@openng/optimus-ui/inputnumber';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { Menu, MenuModule } from '@openng/optimus-ui/menu';
import { SelectModule } from '@openng/optimus-ui/select';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ConfirmationService, MenuItem } from '@openng/optimus-ui/api';
import {
  AbilityService,
  CurrencyFormatPipe,
  DropdownData,
  FileUploadService,
  ImagePreviewItem,
  LinkListDialog,
  rxResourceFixed,
  CurrencyService
} from '@zayan-erp/shared';
import { DsmDeposit, DsmDepositPayment, DsmPaymentImage } from '../../models/dsm-deposit.models';
import { DsmApsDealContextService } from '../../services/dsm-aps-deal-context.service';
import { DsmDealOptionsService } from '../../services/dsm-deal-options.service';
import { DsmDepositService } from '../../services/dsm-deposit.service';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';

type ChequeAttachment = {
  file: File | null;
  name: string;
  s3Key: string;
  documentId: string;
  s3Url: string;
  progress: number | null;
};

type DepositRow = {
  deposit: DsmDeposit;
  payments: DsmDepositPayment[];
  instalmentCount: number;
  isFullyPaid: boolean;
  isOverdue: boolean;
};

@Component({
  selector: 'dsm-aps-deposits-table',
  standalone: true,
  host: { class: 'flex flex-col flex-1 overflow-hidden min-h-0' },
  imports: [
    CheckboxModule,
    DatePickerModule,
    ToggleSwitchModule,
    SelectModule,
    InputNumberModule,
    InputTextModule,
    ButtonModule,
    MenuModule,
    TooltipModule,
    FormsModule,
    ReactiveFormsModule,
    DatePipe,
    NgClass,
    NgTemplateOutlet,
    CurrencyFormatPipe,
    DialogModule,
    FileUploadModule,
    ConfirmDialogModule,
    LinkListDialog,
    FloatLabelModule
  ],
  providers: [ConfirmationService],
  templateUrl: './dsm-aps-deposits-table.component.html'
})
export class DsmApsDepositsTableComponent implements OnChanges {
  deposits = input<DsmDeposit[] | undefined>(undefined);
  /** Shown inside an addendum's "Deposits and Payments" window: no table header, and paid deposits stay editable. */
  addendumView = input(false);

  depositsSaved = output<void>();
  depositPayment = output<{ depositId: string; paymentId?: string }>();
  deleteDeposit = output<void>();
  deleteDepositPayment = output<void>();
  bulkDeleteDeposits = output<string[]>();
  importFrom = output<void>();

  readonly OPTIONS_DEPOSIT_TYPE_LABEL = 'Options';

  readonly depositService = inject(DsmDepositService);
  #currencyService = inject(CurrencyService);
  #abilityService = inject(AbilityService);
  readonly #fileUploadService = inject(FileUploadService);
  readonly #destroyRef = inject(DestroyRef);
  readonly currencyCode = this.#currencyService.currencyCode;
  attachments = signal<ChequeAttachment[]>([]);

  viewImagesDialogVisible = signal(false);
  selectedPaymentImageLinks = signal<DsmPaymentImage[]>([]);

  canBulkDelete(): boolean {
    return this.#abilityService.ability.can('bulk-delete', 'deposit');
  }

  canToggleReturned(): boolean {
    return this.#abilityService.ability.can('toggle-returned', 'deposit');
  }
  #dealOptionsService = inject(DsmDealOptionsService);
  #dealContext = inject(DsmApsDealContextService);
  #confirmationService = inject(ConfirmationService);

  depositMenu = viewChild<Menu>('depositMenu');
  paymentMenu = viewChild<Menu>('paymentMenu');

  form = this.depositService.form;
  depositPaymentForm = this.depositService.depositPaymentForm;
  addendumRequiredValidators = false;
  selectedDeposits: DsmDeposit[] = [];
  activeDepositMenuItems: MenuItem[] = [];
  activePaymentMenuItems: MenuItem[] = [];

  sortedDeposits = computed(() => {
    const deps = this.deposits();
    if (!deps) return [];
    return [...deps].sort((a, b) => new Date(a.depositDueDate).getTime() - new Date(b.depositDueDate).getTime());
  });

  // received/due come from the server (a returned cheque is already excluded), so the table never re-sums payments.
  depositRows = computed<DepositRow[]>(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return this.sortedDeposits().map(deposit => {
      const payments = [...deposit.payments].sort(
        (a, b) =>
          new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime() ||
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      const isFullyPaid = deposit.payments.length > 0 && deposit.due <= 0;
      return {
        deposit,
        payments,
        instalmentCount: payments.filter(payment => !payment.returned).length,
        isFullyPaid,
        isOverdue: deposit.due > 0 && new Date(deposit.depositDueDate).getTime() < startOfToday.getTime()
      };
    });
  });

  expandedDepositIds = signal<ReadonlySet<string>>(new Set());

  toggleExpanded(depositId: string): void {
    this.expandedDepositIds.update(ids => {
      const next = new Set(ids);
      if (!next.delete(depositId)) next.add(depositId);
      return next;
    });
  }

  activePaymentRow(): DepositRow | null {
    return this.depositRows().find(row => row.deposit.id === this.depositService.depositId) ?? null;
  }

  // Plain method, not computed(): depositService.depositId is a mutable property, not a signal,
  // so a computed() here would never invalidate its cache when it changes.
  activePaymentDeposit(): DsmDeposit | null {
    return this.sortedDeposits().find(deposit => deposit.id === this.depositService.depositId) ?? null;
  }

  isDepositSelected(deposit: DsmDeposit): boolean {
    return this.selectedDeposits.some(d => d.id === deposit.id);
  }

  toggleDepositSelection(deposit: DsmDeposit, checked: boolean): void {
    this.selectedDeposits = checked
      ? [...this.selectedDeposits.filter(d => d.id !== deposit.id), deposit]
      : this.selectedDeposits.filter(d => d.id !== deposit.id);
  }

  depositTypes = toSignal(this.depositService.getDepositTypes(), { initialValue: [] as DropdownData[] });

  depositTypeSelectOptions = computed(() =>
    this.depositTypes().map(type => ({
      ...type,
      disabled: type.firstLabel !== this.OPTIONS_DEPOSIT_TYPE_LABEL
    }))
  );

  optionsDepositTypeId = computed(
    () => this.depositTypes().find(t => t.firstLabel === this.OPTIONS_DEPOSIT_TYPE_LABEL)?.id ?? null
  );

  constructor() {
    effect(() => {
      const types = this.depositTypes();
      if (
        this.depositService.addNewDeposits &&
        !this.depositService.depositId &&
        types.length &&
        this.optionsDepositTypeId()
      ) {
        this.applyDefaultOptionsDepositType();
      }
    });
  }

  isOptionsDeposit(deposit: DsmDeposit): boolean {
    return deposit.depositType?.name === this.OPTIONS_DEPOSIT_TYPE_LABEL;
  }

  canRecordPayment(deposit: DsmDeposit): boolean {
    return this.isOptionsDeposit(deposit) && deposit.due > 0;
  }

  canManageDeposit(deposit: DsmDeposit): boolean {
    return this.isOptionsDeposit(deposit) && (!deposit.payments?.length || this.addendumView());
  }

  // What is still owed without the payment this dialog is editing, so editing a cheque never warns about itself.
  // Built from received, not due: due is clamped at 0, which hides the real remainder on an overpaid deposit.
  owedBeforeThisPayment(deposit: DsmDeposit): number {
    const editing = deposit.payments.find(p => p.id === this.depositService.paymentId);
    const paidByOthers = deposit.received - (editing && !editing.returned ? editing.paymentAmount : 0);
    return Math.max(0, deposit.depositAmount - paidByOthers);
  }

  paymentExceedsOwed(deposit: DsmDeposit): boolean {
    const { paymentAmount, returned } = this.depositPaymentForm.getRawValue();
    return !returned && (paymentAmount ?? 0) > this.owedBeforeThisPayment(deposit);
  }

  depositAmountBelowPaid(): boolean {
    const deposit = this.activePaymentDeposit();
    const amount = this.form.getRawValue().depositAmount;
    return !!deposit && deposit.payments.length > 0 && amount != null && amount < deposit.received;
  }

  onAddNewDeposits(): void {
    this.depositService.depositId = null;
    this.depositService.addNewDeposits = true;
    this.applyDefaultOptionsDepositType();
  }

  applyDefaultOptionsDepositType(): void {
    const optionsId = this.optionsDepositTypeId();
    if (!optionsId) return;
    this.form.patchValue({ depositTypeId: optionsId });
    this.onDepositTypeChange(optionsId);
  }

  getAddendums = rxResourceFixed({
    params: () => ({ dealId: this.#dealContext.effectiveDealId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.dealId) {
        return of([]);
      }
      return this.#dealOptionsService.getDealAddendumsForDropdown(params.dealId);
    }
  });

  addendums = computed(() => this.getAddendums.value() ?? []);

  readonly #selectedAddendumId = toSignal(
    this.form.controls.addendumId.valueChanges.pipe(startWith(this.form.controls.addendumId.value)),
    { initialValue: null }
  );

  addendumChoices = computed(() =>
    this.addendums().filter(addendum => addendum.data?.hasValidOptions || addendum.id === this.#selectedAddendumId())
  );

  ngOnChanges(_changes: SimpleChanges): void {
    this.getAddendums.reload();
  }

  resetForm(): void {
    this.form.reset();
    this.depositService.addNewDeposits = false;
  }

  onDepositPaymentFormSubmitted(deposit: DsmDeposit): void {
    const documentIds = this.attachments()
      .filter(a => a.documentId)
      .map(a => a.documentId);
    this.depositService.depositPaymentForm.patchValue({ paymentImageIds: documentIds });

    if (this.depositService.paymentId) {
      this.depositPayment.emit({ depositId: deposit.id, paymentId: this.depositService.paymentId });
    } else {
      this.depositPayment.emit({ depositId: deposit.id });
    }
  }

  onAddPayment(deposit: DsmDeposit): void {
    this.depositService.addPayment = true;
    this.depositService.depositId = deposit.id;
    this.depositService.paymentId = null;
    this.depositPaymentForm.reset();
    this.depositPaymentForm.patchValue({ depositId: deposit.id });
    this.attachments.set([]);
    this.#dealContext.reloadDeal.set(true);
  }

  onCancelPayment(): void {
    this.depositService.addPayment = false;
    this.depositService.depositId = null;
    this.depositService.paymentId = null;
    this.depositPaymentForm.reset();
    this.attachments.set([]);
  }

  getAddendumLabel(addendumId: string | null | undefined): string {
    if (!addendumId) return '';
    const addendum = this.addendums()?.find(a => a?.id === addendumId);
    return addendum?.firstLabel ?? '';
  }

  onEditPayment(payment: DsmDepositPayment): void {
    this.attachments.set(
      (payment.paymentImages ?? []).map(img => ({
        file: null,
        name: 'Saved cheque image',
        s3Key: img.s3Key,
        documentId: img.id,
        s3Url: img.directUrl,
        progress: null
      }))
    );
    this.depositService.addPayment = true;
    this.depositService.depositId = payment.depositId;
    this.depositService.paymentId = payment?.id;
    this.depositPaymentForm.patchValue({
      depositId: payment.depositId,
      paymentDate: new Date(payment.paymentDate),
      paymentAmount: payment.paymentAmount,
      referenceNo: payment.referenceNo,
      returned: payment.returned
    });
  }

  onEditDeposit(deposit: DsmDeposit): void {
    this.depositService.depositId = deposit?.id;
    this.depositService.addNewDeposits = true;
    this.form.patchValue({
      depositTypeId: deposit.depositType.id,
      addendumId: deposit.addendumId,
      taskId: deposit.taskId,
      depositDueDate: new Date(deposit.depositDueDate),
      depositAmount: deposit.depositAmount
    });
  }

  onDeleteDeposit(depositId: string): void {
    this.depositService.depositId = depositId;
    this.deleteDeposit.emit();
  }

  onDeleteDepositPayment(paymentId: string): void {
    this.depositService.paymentId = paymentId;
    this.deleteDepositPayment.emit();
  }

  confirmBulkDelete(): void {
    const count = this.selectedDeposits.length;
    let depositAmount = 0;
    this.selectedDeposits.forEach(deposit => {
      depositAmount += deposit.depositAmount;
    });
    if (count === 0) return;

    this.#confirmationService.confirm({
      header: 'Delete Selected Deposits?',
      message: `Are you sure you want to delete ${count} selected deposit(s) of total amount $${depositAmount}? This action cannot be undone.`,
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: { severity: 'secondary', outlined: false },
      accept: () => {
        const ids = this.selectedDeposits.map(d => d.id);
        this.bulkDeleteDeposits.emit(ids);
        this.selectedDeposits = [];
      }
    });
  }

  onDepositTypeChange(typeId: string | null): void {
    this.form.controls.addendumId.clearValidators();
    this.addendumRequiredValidators = false;
    if (this.depositTypes().find(x => x.id === typeId)?.firstLabel === this.OPTIONS_DEPOSIT_TYPE_LABEL) {
      this.form.controls.addendumId.setValidators([Validators.required]);
      this.addendumRequiredValidators = true;
    }
  }

  isSelectedDepositTypeOptions(): boolean {
    const typeId = this.form.controls.depositTypeId.value;
    const selected = this.depositTypes().find(x => x.id === typeId);
    return selected?.firstLabel === this.OPTIONS_DEPOSIT_TYPE_LABEL;
  }

  onDownloadReceipt(payment: DsmDepositPayment): void {
    const lotNo = this.#dealContext.currentDeal()?.lot?.lotNo;
    this.depositService.downloadReceipt(payment.id, `${lotNo}-Deposit-Slip-${+new Date()}.pdf`).subscribe({
      error: () => {}
    });
  }

  openDepositMenu(event: Event, deposit: DsmDeposit): void {
    if (!this.isOptionsDeposit(deposit)) return;

    this.activeDepositMenuItems = [
      { label: 'Edit', command: () => this.onEditDeposit(deposit) },
      {
        label: 'Delete',
        styleClass: 'text-red-600',
        command: () => this.onDeleteDeposit(deposit.id)
      }
    ];
    this.depositMenu()?.toggle(event);
  }

  openPaymentMenu(event: Event, payment: DsmDepositPayment): void {
    this.activePaymentMenuItems = [
      { label: 'Edit', command: () => this.onEditPayment(payment) },
      {
        label: payment.returned ? 'Mark as Paid' : 'Mark as Returned',
        command: () => this.onToggleReturned(payment)
      },
      { label: 'Download Receipt', command: () => this.onDownloadReceipt(payment) },
      {
        label: 'Delete',
        labelClass: 'text-red-500',
        command: () => this.onDeleteDepositPayment(payment.id)
      }
    ];
    this.paymentMenu()?.toggle(event);
  }

  onToggleReturned(payment: DsmDepositPayment): void {
    this.depositService.paymentId = payment.id;
    this.depositService.depositPaymentForm.patchValue({
      depositId: payment.depositId,
      paymentDate: new Date(payment.paymentDate),
      paymentAmount: payment.paymentAmount,
      referenceNo: payment.referenceNo,
      returned: !payment.returned,
      paymentImageIds: (payment.paymentImages ?? []).map(img => img.id)
    });
    this.depositPayment.emit({ depositId: payment.depositId, paymentId: payment.id });
  }

  onFilesSelected(event: FileUploadHandlerEvent): void {
    event.files.forEach(file => this.#addAttachment(file));
  }

  removeAttachment(attachment: ChequeAttachment): void {
    this.attachments.update(list => list.filter(a => a !== attachment));
  }

  getPaymentImages(payment: DsmDepositPayment): ImagePreviewItem[] {
    return (payment.paymentImages ?? [])
      .filter((img: DsmPaymentImage) => img.directUrl)
      .map((img: DsmPaymentImage) => ({ itemImageSrc: img.directUrl, thumbnailImageSrc: img.directUrl }));
  }

  openPaymentImagesDialog(images: DsmPaymentImage[]): void {
    this.selectedPaymentImageLinks.set(images ?? []);
    this.viewImagesDialogVisible.set(true);
  }

  #addAttachment(file: File): void {
    this.attachments.update(list => [
      ...list,
      { file, name: file.name, s3Key: '', documentId: '', s3Url: '', progress: 0 }
    ]);
    this.#trackUpload(file);
  }

  #trackUpload(file: File): void {
    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3(awsData).pipe(
            tap((uploadEvent: any) => {
              if (uploadEvent?.type === HttpEventType.UploadProgress && uploadEvent.total) {
                const progress = Math.round((100 * uploadEvent.loaded) / uploadEvent.total);
                this.attachments.update(list =>
                  list.map(a =>
                    a.file === file ? { ...a, progress, s3Key: awsData.s3Key, documentId: awsData.documentId } : a
                  )
                );
              }
            })
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.attachments.update(list => list.map(a => (a.file === file ? { ...a, progress: null } : a)));
      });
  }
}
